import { useCallback, useEffect, useMemo, useRef } from 'react';

export const useTimerRegistry = () => {
  const timeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const intervalIdsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());

  const clearTrackedTimeout = useCallback((timerId: ReturnType<typeof setTimeout> | null) => {
    if (!timerId) {
      return;
    }

    clearTimeout(timerId);
    timeoutIdsRef.current.delete(timerId);
  }, []);

  const clearTrackedInterval = useCallback((timerId: ReturnType<typeof setInterval> | null) => {
    if (!timerId) {
      return;
    }

    clearInterval(timerId);
    intervalIdsRef.current.delete(timerId);
  }, []);

  const trackTimeout = useCallback((callback: () => void, delay: number) => {
    const timerId = setTimeout(() => {
      timeoutIdsRef.current.delete(timerId);
      callback();
    }, delay);

    timeoutIdsRef.current.add(timerId);
    return timerId;
  }, []);

  const trackInterval = useCallback((callback: () => void, delay: number) => {
    const timerId = setInterval(callback, delay);
    intervalIdsRef.current.add(timerId);
    return timerId;
  }, []);

  const clearAllTimers = useCallback(() => {
    timeoutIdsRef.current.forEach((timerId) => clearTimeout(timerId));
    intervalIdsRef.current.forEach((timerId) => clearInterval(timerId));
    timeoutIdsRef.current.clear();
    intervalIdsRef.current.clear();
  }, []);

  useEffect(() => clearAllTimers, [clearAllTimers]);

  return useMemo(() => ({
    clearAllTimers,
    clearInterval: clearTrackedInterval,
    clearTimeout: clearTrackedTimeout,
    setInterval: trackInterval,
    setTimeout: trackTimeout
  }), [clearAllTimers, clearTrackedInterval, clearTrackedTimeout, trackInterval, trackTimeout]);
};
